import type { ErpEmployeeAccountDto } from "@/contracts";
import type { AuthUser, Role } from "@/core/auth/session";

export function mapBackendRole(backendRoleKey?: string): Role {
  if (backendRoleKey === "SUPER_ADMIN" || backendRoleKey === "ADMIN") {
    return "ADMIN";
  }
  if (backendRoleKey === "HR_ADMIN" || backendRoleKey === "HR") {
    return "HR";
  }
  if (
    backendRoleKey === "PM" ||
    backendRoleKey === "PROJECT_MANAGER" ||
    backendRoleKey === "MANAGER"
  ) {
    return "PM";
  }
  if (backendRoleKey === "ACCOUNTANT") return "ACCOUNTANT";
  if (backendRoleKey === "ENGINEER") return "ENGINEER";
  return "VIEWER";
}

function getPermissionKeys(employee: ErpEmployeeAccountDto) {
  return [
    ...(employee.permissions ?? []),
    ...(employee.role?.permission_keys ?? []),
    ...(employee.role?.permissions ?? []).map((permission) =>
      typeof permission === "string" ? permission : permission.key
    )
  ].filter((permission): permission is string => Boolean(permission));
}

function getDepartmentBranchId(
  department?: ErpEmployeeAccountDto["department"]
) {
  return (
    department?.branch_id ||
    department?.branchId ||
    department?.branch?.id ||
    department?.branch?.branch_id ||
    department?.branch?.branchId ||
    department?.department?.branch_id ||
    department?.department?.branchId ||
    department?.department?.branch?.id
  );
}

function getAssignedBranchIds(employee: ErpEmployeeAccountDto) {
  const scopes = [employee.scope, employee.scopes].filter(
    (scope): scope is NonNullable<typeof scope> => Boolean(scope)
  );
  const departments = [
    employee.department,
    ...(employee.departments ?? []),
    ...(employee.employee_departments ?? []),
    ...(employee.employeeDepartments ?? []),
    ...scopes.flatMap((scope) => scope.departments ?? [])
  ];

  return [
    employee.branch_id,
    employee.branchId,
    employee.branch?.id,
    employee.branch?.branch_id,
    employee.branch?.branchId,
    ...(employee.branch_ids ?? []),
    ...(employee.branchIds ?? []),
    ...scopes.flatMap((scope) => [
      ...(scope.branch_ids ?? []),
      ...(scope.branchIds ?? [])
    ]),
    ...departments.map((department) =>
      getDepartmentBranchId(department ?? undefined)
    )
  ].filter(
    (branchId, index, branchIds): branchId is string =>
      Boolean(branchId?.trim()) && branchIds.indexOf(branchId) === index
  );
}

function getAssignedDepartmentIds(employee: ErpEmployeeAccountDto) {
  const scopes = [employee.scope, employee.scopes].filter(
    (scope): scope is NonNullable<typeof scope> => Boolean(scope)
  );
  const departments = [
    employee.department,
    ...(employee.departments ?? []),
    ...(employee.employee_departments ?? []),
    ...(employee.employeeDepartments ?? []),
    ...scopes.flatMap((scope) => scope.departments ?? [])
  ].filter((department): department is NonNullable<typeof department> =>
    Boolean(department)
  );

  return [
    employee.department_id,
    employee.departmentId,
    ...(employee.department_ids ?? []),
    ...(employee.departmentIds ?? []),
    ...scopes.flatMap((scope) => [
      ...(scope.department_ids ?? []),
      ...(scope.departmentIds ?? [])
    ]),
    ...departments.map(
      (department) =>
        department.id ||
        department.department_id ||
        department.departmentId ||
        department.department?.id ||
        department.department?.department_id ||
        department.department?.departmentId
    )
  ].filter(
    (departmentId, index, departmentIds): departmentId is string =>
      Boolean(departmentId?.trim()) &&
      departmentIds.indexOf(departmentId) === index
  );
}

export function mergeAuthUserProfile(
  currentUser: AuthUser,
  employee?: ErpEmployeeAccountDto
): AuthUser {
  if (!employee) return currentUser;

  const backendRoleKey =
    employee.role?.key || employee.role_key || currentUser.backendRoleKey;
  const mappedRole = backendRoleKey
    ? mapBackendRole(backendRoleKey)
    : currentUser.role;
  const email = employee.email || currentUser.email;
  const avatarUrl =
    employee.avatar_url ||
    employee.avatarUrl ||
    employee.image_url ||
    employee.imageUrl ||
    currentUser.avatarUrl;
  const webAccessAllowed =
    employee.web_access?.is_allowed ?? currentUser.webAccessAllowed;
  const assignedBranchIds = getAssignedBranchIds(employee);
  const assignedDepartmentIds = getAssignedDepartmentIds(employee);

  const nextUser: AuthUser = {
    ...currentUser,
    username: employee.employee_code || email || currentUser.username,
    fullName:
      employee.full_name || employee.name || email || currentUser.fullName,
    role: mappedRole,
    roleLabel: employee.role?.name || currentUser.roleLabel || mappedRole,
    email,
    ...(employee.id || currentUser.employeeId
      ? { employeeId: employee.id || currentUser.employeeId }
      : {}),
    employeeCode: employee.employee_code || currentUser.employeeCode,
    ...(assignedBranchIds.length
      ? {
          branchId: assignedBranchIds[0],
          branchIds: assignedBranchIds
        }
      : {}),
    ...(assignedDepartmentIds.length
      ? { departmentIds: assignedDepartmentIds }
      : {}),
    phone: employee.phone || currentUser.phone,
    position: employee.position || currentUser.position,
    backendRoleKey,
    profileCompletionRequired:
      employee.profile_completion_required ??
      currentUser.profileCompletionRequired ??
      false,
    profileCompletedAt:
      employee.profile_completed_at ?? currentUser.profileCompletedAt ?? null,
    ...(webAccessAllowed !== undefined ? { webAccessAllowed } : {}),
    permissions: [...new Set(getPermissionKeys(employee))]
  };

  if (avatarUrl) {
    nextUser.avatarUrl = avatarUrl;
  }

  return nextUser;
}

export function buildAuthUser(
  email: string,
  employee?: ErpEmployeeAccountDto
): AuthUser {
  const baseUser: AuthUser = {
    username: email,
    password: "",
    fullName: email,
    role: "VIEWER",
    roleLabel: "VIEWER",
    email
  };

  return mergeAuthUserProfile(baseUser, employee);
}
